import { uniq } from 'lodash'
import moment from 'moment'
import { type FilterObject, convertUrlFiltersToES } from 'app/filter.utils'
import type { PatientLink } from 'app/types/appointment-row'
import type { LocationConsumer, ProviderDirectory, ProviderRecord } from 'app/types/provider-directory'
import type { ContactJson } from 'app/types/entities'
import { filtersToSearch } from 'lib/listSearch'

export type AppointmentsWhen = `today` | `upcoming` | `past` | `all`

export const APPOINTMENTS_WHEN_DEFAULT: AppointmentsWhen = `upcoming`

const APPOINTMENTS_WHEN_VALUES: readonly AppointmentsWhen[] = [`today`, `upcoming`, `past`, `all`]

const DELETED_ROW_EXCLUSION = `!pmsStatus:deleted`

export type WhenSearch = {
  /** Search terms ANDed by whitespace; always includes the deleted-row exclusion when one exists. */
  terms: string[]
  sort: { field: string; direction: `ASC` | `DESC` }
  /** A visible, removable filter the page pre-applies; undefined for past and all. */
  preAppliedFilter?: { attribute: `status`; operator: `not_in`; value: [`Broken`, `Cancelled`] }
}

const NOT_BROKEN_OR_CANCELLED: WhenSearch[`preAppliedFilter`] = {
  attribute: `status`,
  operator: `not_in`,
  value: [`Broken`, `Cancelled`],
}

export function whenSearch(when: AppointmentsWhen, today: Date): WhenSearch {
  const day = moment(today).format(`YYYY-MM-DD`)
  // Day then wall-clock time, so a day reads in clock order across pages (TRK-5846 multi-key sort).
  const field = `date,time`
  switch (when) {
    case `today`:
      return {
        terms: [`date:[${day} TO ${day}]`, DELETED_ROW_EXCLUSION],
        sort: { field, direction: `ASC` },
        preAppliedFilter: NOT_BROKEN_OR_CANCELLED,
      }
    case `upcoming`:
      return {
        terms: [`date:[${day} TO *]`, DELETED_ROW_EXCLUSION],
        sort: { field, direction: `ASC` },
        preAppliedFilter: NOT_BROKEN_OR_CANCELLED,
      }
    case `past`: {
      const yesterday = moment(today).subtract(1, `day`).format(`YYYY-MM-DD`)
      return {
        terms: [`date:[* TO ${yesterday}]`, DELETED_ROW_EXCLUSION],
        sort: { field, direction: `DESC` },
      }
    }
    case `all`:
      return {
        terms: [DELETED_ROW_EXCLUSION],
        sort: { field, direction: `DESC` },
      }
  }
}

export const APPOINTMENT_STATUSES = [`Scheduled`, `Broken`, `Completed`, `Cancelled`] as const

export function appointmentStatusLabel(status: string | null | undefined): string {
  return status || `Booked`
}

export function isAppointmentsWhen(value: string | null): value is AppointmentsWhen {
  return APPOINTMENTS_WHEN_VALUES.includes(value as AppointmentsWhen)
}

const START_FORMAT = `ddd, MMM D [at] h:mm A`
const DAY_FORMAT = `ddd, MMM D`
const START_FORMAT_WITH_YEAR = `ddd, MMM D, YYYY [at] h:mm A`
const DAY_FORMAT_WITH_YEAR = `ddd, MMM D, YYYY`

export function appointmentStartLabel(
  appointment: { date?: string | null; time?: string | null },
  opts?: { withYear?: boolean; today?: Date },
): string {
  if (!appointment.date) return ``
  // `date` is the visit's day at UTC midnight and `time` the practice's clock: parsed together as local, never shifted.
  const day = appointment.date.slice(0, 10)
  // A day outside today's year always carries it, or next year's visits read as out of order.
  const withYear = opts?.withYear || (!!opts?.today && day.slice(0, 4) !== String(opts.today.getFullYear()))
  const startFormat = withYear ? START_FORMAT_WITH_YEAR : START_FORMAT
  const dayFormat = withYear ? DAY_FORMAT_WITH_YEAR : DAY_FORMAT
  return appointment.time
    ? moment(`${day} ${appointment.time}`, `YYYY-MM-DD HH:mm`).format(startFormat)
    : moment(day, `YYYY-MM-DD`).format(dayFormat)
}

export function appointmentPageFilters(
  when: AppointmentsWhen,
  today: Date,
  locationIds: readonly string[],
): FilterObject[] {
  const status = whenSearch(when, today).preAppliedFilter
  return [
    ...(status ? [{ ...status, value: [...status.value], displayName: `Status` } as FilterObject] : []),
    ...(locationIds.length
      ? [{ attribute: `locationId`, operator: `in`, value: [...locationIds], displayName: `Location` } as FilterObject]
      : []),
  ]
}

export function appointmentsSearch(opts: {
  when: AppointmentsWhen
  today: Date
  filtersParam: string | null
  join?: string | null
  pageFilters: readonly FilterObject[]
  patientTerm?: string
  listRule?: string
}): string {
  // Filters in the URL, an emptied `filters=` included, are the user's own and replace the page's defaults, as on Activities.
  const userTerms =
    opts.filtersParam === null ? undefined : convertUrlFiltersToES(opts.filtersParam, opts.join).join(` `)
  const filterTerms = userTerms === undefined ? filtersToSearch([...opts.pageFilters]) : userTerms
  // A saved list's rule is a hard cut the page's own filters only narrow, as on Treatment Plans.
  const listRule = opts.listRule ? `(${opts.listRule})` : undefined
  return [...whenSearch(opts.when, opts.today).terms, listRule, opts.patientTerm, filterTerms].filter(Boolean).join(` `)
}

// Elasticsearch rejects a query past 1024 boolean clauses; the rest of the search needs room too.
export const PATIENT_ID_LIMIT = 500
const NO_PATIENT_MATCH = `patientId:"__no_patient_match__"`

const quotedIds = (patientIds: readonly string[]) =>
  uniq(patientIds)
    .slice(0, PATIENT_ID_LIMIT)
    .map((id) => `"${id.replace(/[\\"]/g, `\\$&`)}"`)
    .join(` OR `)

export function appointmentsForPatients(patientIds: readonly string[]): string {
  return patientIds.length ? `patientId:(${quotedIds(patientIds)})` : NO_PATIENT_MATCH
}

export function contactsForPatients(patientIds: readonly string[]): string {
  return `patientIds:(${quotedIds(patientIds)})`
}

export function patientLinks(
  contacts: readonly Pick<ContactJson, `id` | `name` | `patientIds`>[],
  patientIds: readonly string[],
): Map<string, PatientLink | null> {
  return new Map(
    patientIds.map((patientId) => {
      const contact = contacts.find((candidate) => candidate.patientIds?.includes(patientId))
      return [patientId, contact?.id ? { contactId: contact.id, name: contact.name ?? `` } : null]
    }),
  )
}

const PROVIDER_NAME_SEPARATOR = ` / `

export function providerDirectory(
  providers: readonly ProviderRecord[],
  locations: readonly LocationConsumer[],
): ProviderDirectory {
  // A vendorId such as `provider_3` repeats across a workspace's locations, so a name is keyed by the
  // location's PMS consumer as well; appointments carry the location, providers the consumer.
  const consumerByLocation = new Map(locations.map((location) => [location.id, location.kollaConsumerId]))
  const nameByKey = new Map<string, string>()
  const namesByVendor = new Map<string, string[]>()
  for (const provider of providers) {
    const name = provider.displayName || provider.resourceName
    const vendorNames = namesByVendor.get(provider.vendorId) ?? []
    namesByVendor.set(provider.vendorId, name ? uniq([...vendorNames, name]) : vendorNames)
    if (name) nameByKey.set(`${provider.kollaConsumerId}:${provider.vendorId}`, name)
  }
  return {
    nameAt: (locationId, providerId) => {
      if (!providerId) return undefined
      const consumer = locationId ? consumerByLocation.get(locationId) : undefined
      return (consumer && nameByKey.get(`${consumer}:${providerId}`)) || providerId
    },
    options: [...namesByVendor].map(([vendorId, names]) => ({
      id: vendorId,
      name: names.join(PROVIDER_NAME_SEPARATOR) || vendorId,
      value: vendorId,
    })),
  }
}

export const EMPTY_PROVIDER_DIRECTORY: Readonly<ProviderDirectory> = providerDirectory([], [])
